import { initTimeline } from "../inc/timeline.js";

const mapEl = () => document.getElementById("careers-map");

const buildMarkerContent = (office) => {
    const wrap = document.createElement("div");
    wrap.className = "tilson-marker";
    if (office.corp) {
        wrap.innerHTML =
            '<span class="tilson-marker-corp-ping"></span>' +
            '<span class="tilson-marker-corp"><svg viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M12 2l2.6 7.2H22l-6 4.4 2.3 7.4L12 16.6l-6.3 4.4L8 13.6 2 9.2h7.4z"/></svg></span>';
    } else {
        wrap.innerHTML =
            '<span class="tilson-marker-ping"></span><span class="tilson-marker-dot"></span>';
    }
    return wrap;
};

const createOverlay = (map, office) => {
    const overlay = new google.maps.OverlayView();
    const node = buildMarkerContent(office);
    const tip = document.createElement("span");
    tip.className = "tilson-marker-tip";
    tip.textContent = office.name;
    node.appendChild(tip);
    node.tabIndex = 0;
    node.setAttribute("role", "link");
    node.setAttribute("aria-label", `${office.name} ${office.label}`);
    const go = () => {
        if (office.url) window.location.href = office.url;
    };
    node.addEventListener("click", go);
    node.addEventListener("keydown", (event) => {
        if (event.key === "Enter") go();
    });

    overlay.onAdd = function onAdd() {
        this.getPanes().overlayMouseTarget.appendChild(node);
    };
    overlay.draw = function draw() {
        const point = this.getProjection().fromLatLngToDivPixel(
            new google.maps.LatLng(office.lat, office.lng)
        );
        if (!point) return;
        node.style.position = "absolute";
        node.style.left = `${point.x}px`;
        node.style.top = `${point.y}px`;
    };
    overlay.onRemove = function onRemove() {
        if (node.parentNode) node.parentNode.removeChild(node);
    };
    overlay.setMap(map);
    return overlay;
};

const railEl = () => document.querySelector(".map-section__content");

const RAIL_GUTTER = 32;

const railPadding = () => {
    if (window.matchMedia("(max-width: 991px)").matches) return null;
    const rail = railEl();
    const pane = mapEl();
    if (!rail || !pane) return null;
    const railRect = rail.getBoundingClientRect();
    const paneRect = pane.getBoundingClientRect();
    const left = railRect.right - paneRect.left;
    if (left <= 0) return null;
    return Math.round(left) + RAIL_GUTTER;
};

const correctForRail = (map, offices) => {
    const padding = railPadding();
    if (!padding) return;
    const mapBounds = map.getBounds();
    if (!mapBounds) return;
    const sw = mapBounds.getSouthWest();
    const ne = mapBounds.getNorthEast();
    const west = sw.lng();
    const span = ne.lng() - west;
    if (span <= 0) return;
    const width = mapEl().getBoundingClientRect().width;
    if (!width) return;
    const cutoff = west + (padding / width) * span;
    let minLng = null;
    offices.forEach((office) => {
        if (office.lat < sw.lat() || office.lat > ne.lat()) return;
        if (office.lng < west || office.lng > ne.lng()) return;
        if (minLng === null || office.lng < minLng) minLng = office.lng;
    });
    if (minLng === null || minLng >= cutoff) return;
    const deltaPx = Math.ceil(((cutoff - minLng) / span) * width);
    map.panBy(-deltaPx, 0);
};

const initCareersMap = () => {
    const el = mapEl();
    if (!el) return;
    const offices = window.__CAREERS_OFFICES || [];
    if (!offices.length) return;

    const preload = window.oi_preload || {};
    const styles = preload.careers_map_styles || preload.map_styles || [];
    const map = new google.maps.Map(el, {
        styles,
        disableDefaultUI: true,
        gestureHandling: "cooperative",
        clickableIcons: false,
    });

    const bounds = new google.maps.LatLngBounds();
    offices.forEach((office) => {
        createOverlay(map, office);
        bounds.extend(new google.maps.LatLng(office.lat, office.lng));
    });

    if (offices.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(11);
    } else {
        map.fitBounds(bounds, railPadding() ? 80 : 28);
    }

    google.maps.event.addListener(map, "idle", () => correctForRail(map, offices));
};

window.initCareersMap = initCareersMap;

const loadCareersMap = () => {
    const el = mapEl();
    if (!el) return;
    if (window.google && window.google.maps) {
        initCareersMap();
        return;
    }
    if (document.getElementById("careers-gmaps-js")) return;
    const key = (window.oi_preload && window.oi_preload.map_key) || "";
    const script = document.createElement("script");
    script.id = "careers-gmaps-js";
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&callback=initCareersMap`;
    document.head.appendChild(script);
};

const initCareersMapLazy = () => {
    const el = mapEl();
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
        loadCareersMap();
        return;
    }
    const io = new IntersectionObserver(
        (entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                loadCareersMap();
                obs.disconnect();
            });
        },
        { rootMargin: "200px" }
    );
    io.observe(el);
};

const initCareersSwipers = () => {
    if (!window.Swiper) return;
    document.querySelectorAll("[data-careers-swiper]").forEach((scope) => {
        const el = scope.querySelector(".swiper");
        if (!el) return;
        new window.Swiper(el, {
            slidesPerView: 1,
            slidesPerGroup: 1,
            spaceBetween: 24,
            watchOverflow: true,
            breakpoints: {
                640: { slidesPerView: 2, slidesPerGroup: 2 },
                1024: { slidesPerView: 4, slidesPerGroup: 4 },
            },
            navigation: {
                prevEl: scope.querySelector("[data-careers-prev]"),
                nextEl: scope.querySelector("[data-careers-next]"),
            },
            pagination: {
                el: scope.querySelector("[data-careers-count]"),
                type: "fraction",
                formatFractionCurrent: (n) => (n < 10 ? "0" + n : n),
                formatFractionTotal: (n) => (n < 10 ? "0" + n : n),
            },
        });
    });
};

const initOfficeJump = () => {
    const select = document.querySelector("[data-careers-office-select]");
    if (!select) return;
    select.addEventListener("change", () => {
        if (select.value) window.location.href = select.value;
    });
};

initTimeline();
initCareersMapLazy();
initOfficeJump();
initCareersSwipers();
